import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Trophy, FolderOpen, ListChecks, Wallet, Shield, LogOut, Mail } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/assets", label: "Assets", icon: FolderOpen },
  { path: "/tasks", label: "Tasks", icon: ListChecks },
  { path: "/payouts", label: "Payouts", icon: Wallet },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleNav = () => {
    if (onNavigate) onNavigate();
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-300 ${
      isActive
        ? "bg-[#F7941D]/10 text-[#F7941D] font-medium border border-[#F7941D]/20"
        : "text-white/50 hover:text-white/80 hover:bg-white/[0.04] border border-transparent"
    }`;

  return (
    <aside className="w-64 glass-strong flex flex-col shrink-0 h-full border-r border-white/[0.06]" data-testid="sidebar">
      <div className="p-6 hidden md:block">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#F7941D] flex items-center justify-center glow-orange">
            <span className="text-white text-xs font-bold">E</span>
          </div>
          <span className="text-lg font-semibold text-white/90 tracking-tight">Eamico</span>
        </div>
        <p className="text-xs text-white/30 mt-1.5 tracking-wide">Ambassador Portal</p>
      </div>
      <div className="pt-14 md:pt-0" />
      <Separator className="bg-white/[0.06] hidden md:block" />
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(({ path, label, icon: Icon }) => (
          <NavLink key={path} to={path} className={linkClass} onClick={handleNav} data-testid={`nav-${label.toLowerCase()}`}>
            <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <>
          <NavLink to="/marketing" className={linkClass} onClick={handleNav} data-testid="nav-marketing">
            <Mail className="w-[18px] h-[18px]" strokeWidth={1.5} />
            Marketing
          </NavLink>
          <NavLink to="/admin" className={linkClass} onClick={handleNav} data-testid="nav-admin">
            <Shield className="w-[18px] h-[18px]" strokeWidth={1.5} />
            Admin
          </NavLink>
          </>
        )}
      </nav>
      <div className="p-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 mb-3 px-3">
          <div className="w-8 h-8 rounded-full bg-[#F7941D]/15 flex items-center justify-center text-xs font-semibold text-[#F7941D]">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white/85 truncate">{user?.name}</p>
            <p className="text-xs text-white/30 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-all duration-300"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          Logout
        </button>
      </div>
    </aside>
  );
}
